import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Menu } from "@base-ui/react/menu"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarDays, Clock, Copy, GitFork, Loader2, Lock, MapPin, MoreHorizontal, Pencil, Rows3, Trash2, Users } from "lucide-react"
import { toast } from "sonner"
import { ActionBar } from "@/components/ui/ActionBar"
import { TitleBar } from "@/components/ui/TitleBar"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { AgendaWizard } from "@/components/schedule/wizard/AgendaWizard"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { normalizeWallClockDateTime } from "@/core/local-datetime"
import { useRightRail } from "@/lib/right-rail"
import {
  agendaActivitiesQueryOptions,
  agendaActivitiesKey,
  agendaGroupsKey,
  agendaGroupsQueryOptions,
  agendaItemsKey,
  agendaItemsQueryOptions,
  attendanceChip,
  choiceLabel,
  clusterByStartTime,
  commonSlotActivity,
  commonSlotGroup,
  deleteAgendaGroup,
  deleteAgendaGroupDryRun,
  deleteAgendaActivity,
  deleteAgendaActivityDryRun,
  duplicateAgendaGroup,
  formatDayLong,
  formatDayMonth,
  formatTime,
  formatWeekday,
  groupByDay,
  locationPath,
  locationsQueryOptions,
  updateActivity,
  updateGroup,
  type AgendaActivity,
  type EmptyAgendaGroup,
  type AgendaGroup,
  type AgendaItem,
  type ChipTone,
  type LocationRow,
} from "@/core/schedule"
import {
  applyGridLayout,
  buildGridColumns,
  gridLayoutsEqual,
  reconcileGridLayout,
  readSchedulePrefs,
  writeSchedulePrefs,
  type GridLayoutState,
} from "@/components/schedule/grid-layout"
import { ScheduleGrid } from "@/components/schedule/ScheduleGrid"
import { SessionRecord } from "@/components/schedule/SessionRecord"
import { DateTimePicker } from "@/components/ui/DateTimePicker"
import { ContainedDialog } from "@/components/ui/ContainedDialog"

// Layout options for the shared View dropdown.
const SCHEDULE_VIEW_MODES = [
  { id: "list", label: "Timeline", icon: <Clock className="mb-mii" /> },
  { id: "grid", label: "Grid", icon: <Rows3 className="mb-mii" /> },
]

// The Schedule LAYOUT route. Route DEPTH = Main columns (mirrors forms.tsx):
//   /schedule                 → col 1 (timeline / grid)
//   /schedule/$agendaItemId   → col 1 + 2 (+ session record)
// `$agendaItemId` lives on a MARKER child route; ColumnLayout decides how many
// fit. `?focus=2` fullscreens col 2. Legacy `?sessionId=…` redirects into path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/schedule")({
  validateSearch: (
    s: Record<string, unknown>,
  ): { focus?: number; view?: "list" | "grid"; sessionId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      view: s.view === "grid" ? "grid" : s.view === "list" ? "list" : undefined,
      sessionId: typeof s.sessionId === "string" ? s.sessionId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.sessionId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/schedule/$agendaItemId",
        params: { ...params, agendaItemId: search.sessionId },
        search: { focus: search.focus, view: search.view },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      const { queryClient } = context
      // Schedule renders from two essential lists: the agenda items + the
      // groups they cluster into. Warm both fire-and-forget.
      void queryClient.ensureQueryData(agendaItemsQueryOptions(eventId)).catch(() => undefined)
      void queryClient.ensureQueryData(agendaGroupsQueryOptions(eventId)).catch(() => undefined)
    })
  },
  component: ScheduleView,
})

function ScheduleView() {
  const queryClient = useQueryClient()
  const params = useParams({ from: "/$orgSlug/$eventSlug/schedule" })
  const { focus, view: viewParam } = Route.useSearch()
  const view = viewParam ?? "list"
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const showInRail = useRightRail((s) => s.show)

  const { agendaItemId: sessionId } = useRouteRecordSelection("agendaItemId")

  // Clicking a location anywhere in the schedule opens its full record in the
  // right rail (mirrors guests).
  const openLocation = useCallback(
    (locationId: string, name: string) => showInRail({ kind: "location", locationId, name }),
    [showInRail],
  )

  // Changing view PRESERVES the open session (it lives on the path, not search).
  const setView = useCallback(
    (next: "list" | "grid") => {
      if (sessionId)
        void navigate({
          to: "/$orgSlug/$eventSlug/schedule/$agendaItemId",
          params: { ...params, agendaItemId: sessionId },
          search: { view: next, focus },
        })
      else void navigate({ to: "/$orgSlug/$eventSlug/schedule", params, search: { view: next, focus } })
    },
    [navigate, params, sessionId, focus],
  )
  const openSession = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/schedule/$agendaItemId",
        params: { ...params, agendaItemId: id },
        search: { view },
      }),
    [navigate, params, view],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/schedule", params, search: { view } }),
    [navigate, params, view],
  )

  const itemsQ = useQuery({ ...agendaItemsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const groupsQ = useQuery({ ...agendaGroupsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const activitiesQ = useQuery({ ...agendaActivitiesQueryOptions(eventId ?? ""), enabled: !!eventId })
  const locationsQ = useQuery({ ...locationsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const duplicateGroupMut = useMutation({
    mutationFn: (groupId: string) => duplicateAgendaGroup(groupId),
    onSuccess: (result) => {
      toast.success(`Duplicated ${result.group.name}`)
      if (!eventId) return
      void queryClient.invalidateQueries({ queryKey: agendaGroupsKey(eventId) })
      void queryClient.invalidateQueries({ queryKey: agendaItemsKey(eventId) })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Duplicate failed"),
  })
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null)
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)
  const [deletingActivityId, setDeletingActivityId] = useState<string | null>(null)

  const groupsById = useMemo(() => {
    const m = new Map<string, AgendaGroup>()
    for (const g of groupsQ.data ?? []) m.set(g.id, g)
    return m
  }, [groupsQ.data])
  const editingGroup = editingGroupId ? groupsById.get(editingGroupId) ?? null : null
  const deletingGroup = deletingGroupId ? groupsById.get(deletingGroupId) ?? null : null
  const activitiesById = useMemo(() => {
    const m = new Map<string, AgendaActivity>()
    for (const a of activitiesQ.data ?? []) m.set(a.id, a)
    return m
  }, [activitiesQ.data])
  const editingActivity = editingActivityId ? activitiesById.get(editingActivityId) ?? null : null
  const deletingActivity = deletingActivityId ? activitiesById.get(deletingActivityId) ?? null : null
  const locationsById = useMemo(() => {
    const m = new Map<string, LocationRow>()
    for (const l of locationsQ.data ?? []) m.set(l.id, l)
    return m
  }, [locationsQ.data])

  // Drafts + cancelled hidden by default; the toggle reveals them.
  const rawItems = useMemo(() => itemsQ.data ?? [], [itemsQ.data])
  const [showInactive, setShowInactive] = useState(true)
  const inactiveCount = useMemo(
    () => rawItems.filter((i) => i.status !== "confirmed").length,
    [rawItems],
  )
  const items = useMemo(
    () => (showInactive ? rawItems : rawItems.filter((i) => i.status === "confirmed")),
    [rawItems, showInactive],
  )

  // Action-bar search + location filter.
  const [query, setQuery] = useState("")
  const [locationFilter, setLocationFilter] = useState<string[]>([])
  const filteredItems = useMemo(() => {
    const qq = query.trim().toLowerCase()
    return items.filter((i) => {
      if (locationFilter.length > 0 && !locationFilter.includes(i.locationId ?? "")) return false
      if (!qq) return true
      const loc = i.locationId ? locationsById.get(i.locationId) : null
      return [i.name, i.description, loc?.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(qq)
    })
  }, [items, query, locationFilter, locationsById])

  const emptyGroups = useMemo<EmptyAgendaGroup[]>(() => {
    if (!showInactive) return []
    if (locationFilter.length > 0) return []
    const qq = query.trim().toLowerCase()
    const childGroupIds = new Set(rawItems.map((item) => item.groupId).filter(Boolean) as string[])
    return (groupsQ.data ?? []).filter((group): group is EmptyAgendaGroup => {
      if (!group.startsAt) return false
      if (childGroupIds.has(group.id)) return false
      if (!qq) return true
      return [group.name, group.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(qq)
    })
  }, [groupsQ.data, locationFilter.length, query, rawItems, showInactive])

  const dayGroups = useMemo(() => groupByDay(filteredItems, emptyGroups), [filteredItems, emptyGroups])

  const [activeDay, setActiveDay] = useState<string | null>(null)
  useEffect(() => {
    if (dayGroups.length === 0) {
      setActiveDay(null)
      return
    }
    setActiveDay((prev) =>
      prev && dayGroups.some((d) => d.date === prev) ? prev : dayGroups[0]?.date ?? null,
    )
  }, [dayGroups])

  const activeGroup = useMemo(
    () => dayGroups.find((d) => d.date === activeDay) ?? dayGroups[0] ?? null,
    [dayGroups, activeDay],
  )

  // ── list-view scroll-spy ────────────────────────────────────────────────
  // In list/timeline view the whole schedule renders stacked; the day tabs
  // jump to a day and highlight whichever day is currently in view.
  const listScrollRef = useRef<HTMLDivElement>(null)
  const dayEls = useRef<Map<string, HTMLDivElement>>(new Map())
  // When a day tab is clicked we kick off a smooth scroll. The spy keeps firing
  // during that animation and would yank the highlight back to the current
  // scroll position — so we stash the target day here and suppress spy updates
  // until the target actually reaches the top (or a timeout fallback fires, in
  // case `scrollend` never lands on the exact offset).
  const programmaticTarget = useRef<string | null>(null)
  const programmaticTimer = useRef<number | null>(null)
  const clearProgrammatic = useCallback(() => {
    programmaticTarget.current = null
    if (programmaticTimer.current != null) {
      window.clearTimeout(programmaticTimer.current)
      programmaticTimer.current = null
    }
  }, [])
  const scrollToDay = useCallback(
    (date: string) => {
      setActiveDay(date)
      const el = dayEls.current.get(date)
      const sc = listScrollRef.current
      if (!el || !sc) return
      // Guard the spy until we arrive at this day's section.
      programmaticTarget.current = date
      if (programmaticTimer.current != null) window.clearTimeout(programmaticTimer.current)
      programmaticTimer.current = window.setTimeout(() => {
        programmaticTarget.current = null
        programmaticTimer.current = null
      }, 1000)
      sc.scrollTo({ top: el.offsetTop - 8, behavior: "smooth" })
    },
    [],
  )
  const onListScroll = useCallback(() => {
    const sc = listScrollRef.current
    if (!sc) return
    const top = sc.scrollTop + 24
    let current: string | null = null
    for (const [date, el] of dayEls.current) {
      if (el.offsetTop <= top) current = date
    }
    if (!current) return
    const target = programmaticTarget.current
    if (target) {
      // While a programmatic scroll is in flight, ignore everything except the
      // moment the target day reaches the threshold — then resume the spy.
      if (current === target) clearProgrammatic()
      else return
    }
    setActiveDay((prev) => (prev === current ? prev : current))
  }, [clearProgrammatic])
  // Tidy up the fallback timer on unmount.
  useEffect(() => () => clearProgrammatic(), [clearProgrammatic])
  const onDayTab = (date: string) => (view === "list" ? scrollToDay(date) : setActiveDay(date))

  // ── grid column layout (order / hide / collapse), persisted per event ──────
  const [gridLayout, setGridLayout] = useState<GridLayoutState>({
    order: [],
    hidden: [],
    collapsed: [],
  })
  const [prefsHydrated, setPrefsHydrated] = useState(false)

  useEffect(() => {
    if (!eventId) {
      setGridLayout({ order: [], hidden: [], collapsed: [] })
      setPrefsHydrated(false)
      return
    }
    const prefs = readSchedulePrefs(eventId)
    setGridLayout({
      order: prefs?.order ?? [],
      hidden: prefs?.hidden ?? [],
      collapsed: prefs?.collapsed ?? [],
    })
    setPrefsHydrated(true)
  }, [eventId])

  const baseGridColumns = useMemo(
    () => buildGridColumns(items, locationsQ.data ?? [], locationsById, locationFilter),
    [items, locationsQ.data, locationsById, locationFilter],
  )
  const gridColumns = useMemo(
    () => applyGridLayout(baseGridColumns, gridLayout),
    [baseGridColumns, gridLayout],
  )
  const visibleGridColumns = useMemo(
    () =>
      gridColumns.filter(
        (c) =>
          !c.hidden &&
          // When the Location filter is active, only show the picked location
          // lanes — unselected locations (and the unassigned lane) drop out.
          (locationFilter.length === 0 ||
            (c.locationId != null && locationFilter.includes(c.locationId))),
      ),
    [gridColumns, locationFilter],
  )

  useEffect(() => {
    if (baseGridColumns.length === 0) return
    const baseKeys = baseGridColumns.map((c) => c.key)
    setGridLayout((prev) => {
      const next = reconcileGridLayout(prev, baseKeys)
      return gridLayoutsEqual(prev, next) ? prev : next
    })
  }, [baseGridColumns])

  useEffect(() => {
    if (!eventId || !prefsHydrated) return
    writeSchedulePrefs(eventId, {
      order: gridLayout.order,
      hidden: gridLayout.hidden,
      collapsed: gridLayout.collapsed,
    })
  }, [eventId, prefsHydrated, gridLayout])

  function toggleCollapsed(key: string) {
    const baseKeys = baseGridColumns.map((c) => c.key)
    setGridLayout((prev) => {
      const next = reconcileGridLayout(prev, baseKeys)
      const collapsed = next.collapsed.includes(key)
        ? next.collapsed.filter((e) => e !== key)
        : [...next.collapsed, key]
      return { ...next, collapsed }
    })
  }

  const loading =
    !!eventId &&
    (itemsQ.isLoading || groupsQ.isLoading || locationsQ.isLoading || activitiesQ.isLoading)
  const error = itemsQ.isError
    ? ((itemsQ.error as Error)?.message ?? "Failed to load schedule")
    : null

  // "Add to schedule" wizard opens as an inline column (see the columns block
  // below), not a modal — preselects the day the admin is currently on.
  const [createOpen, setCreateOpen] = useState(false)
  // Close the create column and drop any ?focus=2 fullscreen it set.
  const closeCreate = useCallback(() => {
    setCreateOpen(false)
    if (focus) void navigate({ to: "/$orgSlug/$eventSlug/schedule", params, search: { view } })
  }, [focus, navigate, params, view])

  // Days drive the action bar's DATE segment (replacing quick views here).
  // Day-of-week stacked above the date; no item counts.
  const days = dayGroups.map((g) => ({
    id: g.date,
    label: (
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[10px] font-semibold tracking-wide uppercase opacity-65">
          {formatWeekday(g.date)}
        </span>
        <span>{formatDayMonth(g.date)}</span>
      </span>
    ),
  }))

  const main = (
    <div className="relative flex h-full flex-col">
      <TitleBar
        title="Activities"
        onNew={
          eventId
            ? () => {
                setCreateOpen(true)
                // If a session is open in the right panel, clear it so the new
                // session wizard REPLACES that card in the same column — keep
                // ?focus so a fullscreened panel stays fullscreened on the wizard.
                if (sessionId)
                  void navigate({
                    to: "/$orgSlug/$eventSlug/schedule",
                    params,
                    search: { view, focus },
                  })
              }
            : undefined
        }
        newLabel="Add activity"
      />
      <ActionBar
        title="Activities"
        views={days}
        viewsVariant="date"
        activeView={activeDay ?? ""}
        onView={(id) => onDayTab(id)}
        search={query}
        onSearch={setQuery}
        filters={[
          ...(locationsQ.data && locationsQ.data.length > 0
            ? [
                {
                  id: "location",
                  label: "Location",
                  options: [
                    ["all", "All locations"] as const,
                    ...locationsQ.data.map((l) => [l.id, l.name] as const),
                  ],
                  value: "all",
                  multi: true,
                  selected: locationFilter,
                  // Grid view only: each shown location gets a Wide/Thin column
                  // toggle on the right of its filter row.
                  optionAside:
                    view === "grid"
                      ? (locId: string) =>
                          locId === "all" ? null : (
                            <WideThinToggle
                              collapsed={gridLayout.collapsed.includes(locId)}
                              onToggle={() => toggleCollapsed(locId)}
                            />
                          )
                      : undefined,
                },
              ]
            : []),
          ...(inactiveCount > 0
            ? [
                {
                  id: "status",
                  label: "Status",
                  options: [
                    ["active", "Active"] as const,
                    ["all", `Include ${inactiveCount} draft/cancelled`] as const,
                  ],
                  value: showInactive ? "all" : "active",
                },
              ]
            : []),
        ]}
        onFilter={(id, v) => {
          if (id === "location") {
            if (v === "all") setLocationFilter([])
            else setLocationFilter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
          } else if (id === "status") setShowInactive(v === "all")
        }}
        onClearFilters={() => {
          setLocationFilter([])
          setShowInactive(true)
        }}
        displayModes={SCHEDULE_VIEW_MODES}
        displayMode={view}
        onDisplayMode={(id) => setView(id as "list" | "grid")}
      />

      {/* BODY */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {!eventId || loading ? (
          <LoadingState />
        ) : error ? (
          <CenteredMessage
            icon={<CalendarDays className="h-5 w-5" />}
            title="Couldn't load the schedule"
            blurb={error}
          />
        ) : dayGroups.length === 0 ? (
          <CenteredMessage
            icon={<CalendarDays className="h-5 w-5" />}
            title="No agenda items yet"
            blurb="Activities, meals, and scheduled sessions appear here as a timeline or a location grid."
          />
        ) : view === "grid" ? (
          <div className="flex h-full min-h-0 flex-col">
            {visibleGridColumns.length === 0 ? (
              <CenteredMessage
                icon={<CalendarDays className="h-5 w-5" />}
                title="No location lanes to show"
                blurb="Pick a location in the Location filter to bring the schedule grid back."
              />
            ) : activeGroup ? (
              <ScheduleGrid
                date={activeGroup.date}
                items={activeGroup.items}
                columns={visibleGridColumns}
                groupsById={groupsById}
                activitiesById={activitiesById}
                onItemClick={(item) => openSession(item.id)}
                onLocationClick={openLocation}
              />
            ) : null}
          </div>
        ) : (
          /* LIST/TIMELINE — the whole schedule renders stacked; the day-tab row
             above acts as a scroll-spy. */
          <div className="flex h-full min-h-0">
            <div
              ref={listScrollRef}
              onScroll={onListScroll}
              className="scroll-thin min-h-0 flex-1 overflow-y-auto"
            >
              <div className="mx-auto max-w-[920px] space-y-6 px-5 py-4">
                {dayGroups.map((g) => (
                  <div
                    key={g.date}
                    ref={(el) => {
                      if (el) dayEls.current.set(g.date, el)
                      else dayEls.current.delete(g.date)
                    }}
                  >
                    <DaySection
                      date={g.date}
                      items={g.items}
                      emptyGroups={g.emptyGroups}
                      groupsById={groupsById}
                      activitiesById={activitiesById}
                      locationsById={locationsById}
                      onItemClick={(item) => openSession(item.id)}
                      onLocationClick={openLocation}
                      onDuplicateGroup={(group) => duplicateGroupMut.mutate(group.id)}
                      onEditGroup={(group) => setEditingGroupId(group.id)}
                      onDeleteGroup={(group) => setDeletingGroupId(group.id)}
                      onEditActivity={(activity) => setEditingActivityId(activity.id)}
                      onDeleteActivity={(activity) => setDeletingActivityId(activity.id)}
                      duplicatingGroupId={
                        duplicateGroupMut.isPending ? duplicateGroupMut.variables : null
                      }
                      activeId={sessionId}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {deletingGroup && (
        <DeleteGroupDialog
          open={!!deletingGroup}
          onOpenChange={(open) => !open && setDeletingGroupId(null)}
          group={deletingGroup}
          onDeleted={() => {
            setDeletingGroupId(null)
            if (!eventId) return
            void queryClient.invalidateQueries({ queryKey: agendaGroupsKey(eventId) })
            void queryClient.invalidateQueries({ queryKey: agendaItemsKey(eventId) })
          }}
        />
      )}
      {deletingActivity && (
        <DeleteActivityDialog
          open={!!deletingActivity}
          onOpenChange={(open) => !open && setDeletingActivityId(null)}
          activity={deletingActivity}
          onDeleted={() => {
            setDeletingActivityId(null)
            if (!eventId) return
            void queryClient.invalidateQueries({ queryKey: agendaActivitiesKey(eventId) })
            void queryClient.invalidateQueries({ queryKey: agendaItemsKey(eventId) })
          }}
        />
      )}
    </div>
  )

  const columns: ColumnSpec[] = [{ id: "schedule", minWidth: 320, fill: true, node: main }]
  if (sessionId) {
    columns.push({
      id: "session",
      minWidth: 520,
      width: 520,
      node: <SessionRecord agendaItemId={sessionId} onBack={closePane} />,
    })
  } else if (editingGroup && eventId) {
    columns.push({
      id: "group",
      minWidth: 440,
      width: 480,
      node: (
        <BlockEditPanel
          group={editingGroup}
          onClose={() => setEditingGroupId(null)}
          onSaved={() => {
            setEditingGroupId(null)
            void queryClient.invalidateQueries({ queryKey: agendaGroupsKey(eventId) })
            void queryClient.invalidateQueries({ queryKey: agendaItemsKey(eventId) })
          }}
        />
      ),
    })
  } else if (editingActivity && eventId) {
    columns.push({
      id: "activity",
      minWidth: 440,
      width: 480,
      node: (
        <ActivityEditPanel
          activity={editingActivity}
          onClose={() => setEditingActivityId(null)}
          onSaved={() => {
            setEditingActivityId(null)
            void queryClient.invalidateQueries({ queryKey: agendaActivitiesKey(eventId) })
            void queryClient.invalidateQueries({ queryKey: agendaItemsKey(eventId) })
          }}
        />
      ),
    })
  } else if (createOpen && eventId) {
    // The "Add to schedule" wizard opens as an INLINE column beside the schedule
    // (mirrors the session record), not a modal sheet. ColumnLayout shows it
    // alongside the list while there's room and collapses to it full-width when
    // narrow; the TitleBar's ⤢ toggles ?focus=2 to fullscreen it on demand.
    columns.push({
      id: "create",
      minWidth: 520,
      width: 560,
      node: (
        <div className="flex h-full min-h-0 flex-col bg-white dark:bg-surface">
          <TitleBar
            title="Add to schedule"
            subtitle="Pick a recipe — drafts stay hidden until you publish"
            onClose={closeCreate}
            onFullscreen={() =>
              void navigate({
                to: "/$orgSlug/$eventSlug/schedule",
                params,
                search: { view, focus: focus === 2 ? undefined : 2 },
              })
            }
          />
          <div className="min-h-0 flex-1 px-4 pt-2 pb-3">
            <AgendaWizard
              eventId={eventId}
              defaultDay={activeDay ?? undefined}
              onDone={closeCreate}
              onCancel={closeCreate}
            />
          </div>
        </div>
      ),
    })
  }

  return <RoutedCollectionLayout columns={columns} focus={focus} />
}

// Wide/Thin column toggle shown on the right of each Location filter row (grid
// view). Thin = collapsed lane (initials only); Wide = full lane.
function WideThinToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const seg = (active: boolean) =>
    `px-1.5 py-0.5 transition ${
      active
        ? "bg-[var(--accent)] text-white"
        : "text-plum-700/55 hover:text-plum-800 dark:text-plum-200/45 dark:hover:text-plum-50"
    }`
  return (
    <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-plum-200/70 text-[10px] font-semibold dark:border-white/10">
      <button
        type="button"
        className={seg(!collapsed)}
        onClick={(e) => {
          e.stopPropagation()
          if (collapsed) onToggle()
        }}
      >
        Wide
      </button>
      <button
        type="button"
        className={`border-l border-plum-200/70 dark:border-white/10 ${seg(collapsed)}`}
        onClick={(e) => {
          e.stopPropagation()
          if (!collapsed) onToggle()
        }}
      >
        Thin
      </button>
    </span>
  )
}

function BlockEditPanel({
  group,
  onClose,
  onSaved,
}: {
  group: AgendaGroup
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? "")
  const [startsAt, setStartsAt] = useState(toPickerValue(group.startsAt ?? null))
  const [endsAt, setEndsAt] = useState(toPickerValue(group.endsAt ?? null))
  const isPickOne = group.selectionRule !== "multi"
  const [isRequired, setIsRequired] = useState((group.isRequired ?? 0) === 1)
  const [allowNone, setAllowNone] = useState(isPickOne && (group.allowNone ?? 0) === 1)
  const [shiftChildren, setShiftChildren] = useState(true)

  const errors: string[] = []
  if (!name.trim()) errors.push("Name is required.")
  if (startsAt && endsAt && toActionDateTime(endsAt)! <= toActionDateTime(startsAt)!) {
    errors.push("End must be after start.")
  }
  const valid = errors.length === 0

  const saveMut = useMutation({
    mutationFn: () =>
      updateGroup({
        id: group.id,
        name: name.trim(),
        description: description.trim() || null,
        isRequired: isRequired ? 1 : 0,
        allowNone: isPickOne && allowNone ? 1 : 0,
        startsAt: toActionDateTime(startsAt),
        endsAt: toActionDateTime(endsAt),
        shiftChildren,
      }),
    onSuccess: () => {
      toast.success("Block saved")
      onSaved()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save block"),
  })

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-surface">
      <TitleBar title="Edit block" subtitle={group.name} onClose={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) saveMut.mutate()
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="scroll-thin min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <Field label="Name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="e.g. Block 1"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`${inputCls} min-h-[84px] resize-none py-2 leading-relaxed`}
              placeholder="Optional internal/context copy"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Starts">
              <DateTimePicker
                value={startsAt}
                onChange={setStartsAt}
                mode="datetime"
                placeholder="No start"
              />
            </Field>
            <Field label="Ends">
              <DateTimePicker
                value={endsAt}
                onChange={setEndsAt}
                mode="datetime"
                placeholder="No end"
              />
            </Field>
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-plum-200/70 bg-plum-50/40 p-3 text-[12px] text-plum-700/65 dark:border-white/8 dark:bg-white/[0.03] dark:text-plum-200/50">
            <input
              type="checkbox"
              checked={shiftChildren}
              onChange={(e) => setShiftChildren(e.target.checked)}
              className="mt-0.5"
            />
            <span>Move child sessions when the block start time changes.</span>
          </label>
          <Field label="Block type">
            <div className="inline-flex h-9 items-center rounded-lg border border-plum-200/70 bg-plum-50 px-3 text-[13px] font-medium text-plum-800 dark:border-white/10 dark:bg-white/[0.04] dark:text-plum-100">
              {isPickOne ? "Pick one" : "Pick any"}
            </div>
          </Field>
          <div className="grid gap-2">
            <CheckRow label="Required block" checked={isRequired} onChange={setIsRequired} />
            <CheckRow
              label="Allow none"
              checked={allowNone}
              disabled={!isPickOne}
              onChange={setAllowNone}
            />
          </div>
          {errors.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {errors[0]}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-plum-200/50 px-4 py-3 dark:border-white/8">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg px-3.5 text-[13px] font-medium text-plum-700/70 transition hover:bg-plum-100/70 dark:text-plum-200/60 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || saveMut.isPending}
            className="flex h-9 items-center gap-1.5 rounded-lg px-4 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: "var(--accent)" }}
          >
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </div>
  )
}

function ActivityEditPanel({
  activity,
  onClose,
  onSaved,
}: {
  activity: AgendaActivity
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(activity.name)
  const [description, setDescription] = useState(activity.description ?? "")
  const [minValue, setMinValue] = useState(String(activity.minPerAttendee ?? 0))
  const [maxValue, setMaxValue] = useState(String(activity.maxPerAttendee ?? 1))
  const [isRequired, setIsRequired] = useState((activity.isRequired ?? 0) === 1)

  const min = Number.parseInt(minValue, 10)
  const max = Number.parseInt(maxValue, 10)
  const minValid = Number.isInteger(min) && min >= 0
  const maxValid = Number.isInteger(max) && max >= 1
  const allowNone = minValid && min === 0

  const errors: string[] = []
  if (!name.trim()) errors.push("Name is required.")
  if (!minValid) errors.push("Minimum must be 0 or more.")
  if (!maxValid) errors.push("Maximum must be 1 or more.")
  if (minValid && maxValid && min > max) errors.push("Minimum cannot be greater than maximum.")
  const valid = errors.length === 0

  const saveMut = useMutation({
    mutationFn: () =>
      updateActivity({
        id: activity.id,
        name: name.trim(),
        description: description.trim() || null,
        minPerAttendee: min,
        maxPerAttendee: max,
        isRequired: isRequired ? 1 : 0,
      }),
    onSuccess: () => {
      toast.success("Activity block saved")
      onSaved()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save activity block"),
  })

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-surface">
      <TitleBar title="Edit activity block" subtitle={activity.name} onClose={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) saveMut.mutate()
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="scroll-thin min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <Field label="Name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="e.g. Spa appointments"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`${inputCls} min-h-[84px] resize-none py-2 leading-relaxed`}
              placeholder="Optional internal/context copy"
            />
          </Field>
          <Field label="Block type">
            <div className="inline-flex h-9 items-center rounded-lg border border-plum-200/70 bg-plum-50 px-3 text-[13px] font-medium text-plum-800 dark:border-white/10 dark:bg-white/[0.04] dark:text-plum-100">
              Activity quota
            </div>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Minimum selections" required>
              <input
                type="number"
                min={0}
                step={1}
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Maximum selections" required>
              <input
                type="number"
                min={1}
                step={1}
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid gap-2">
            <CheckRow label="Required block" checked={isRequired} onChange={setIsRequired} />
            <CheckRow
              label="Allow none"
              checked={allowNone}
              onChange={(checked) => {
                if (checked) setMinValue("0")
                else if (!minValid || min === 0) setMinValue("1")
              }}
            />
          </div>
          {errors.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {errors[0]}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-plum-200/50 px-4 py-3 dark:border-white/8">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg px-3.5 text-[13px] font-medium text-plum-700/70 transition hover:bg-plum-100/70 dark:text-plum-200/60 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || saveMut.isPending}
            className="flex h-9 items-center gap-1.5 rounded-lg px-4 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: "var(--accent)" }}
          >
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </div>
  )
}

function DeleteGroupDialog({
  open,
  onOpenChange,
  group,
  onDeleted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: AgendaGroup
  onDeleted: () => void
}) {
  const preview = useQuery({
    queryKey: ["agenda-group-delete-preview", group.id],
    enabled: open,
    queryFn: () => deleteAgendaGroupDryRun(group.id),
    staleTime: 0,
  })
  const deleteMut = useMutation({
    mutationFn: () => deleteAgendaGroup(group.id),
    onSuccess: () => {
      toast.success("Block deleted")
      onDeleted()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  })
  const impact = preview.data?.wouldDelete

  return (
    <ContainedDialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={`Delete “${group.name}”?`}
      width={448}
      icon={
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
          <Trash2 className="h-[16px] w-[16px]" />
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-lg px-3.5 text-[13px] font-medium text-plum-700/70 transition hover:bg-plum-100/70 dark:text-plum-200/60 dark:hover:bg-white/5"
          >
            Keep block
          </button>
          <button
            type="button"
            onClick={() => deleteMut.mutate()}
            disabled={deleteMut.isPending}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-rose-600 px-4 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-[13px] leading-relaxed text-plum-700/65 dark:text-plum-200/55">
          This permanently removes the block and its child sessions from the schedule.
        </p>
        <DeleteImpactRows loading={preview.isLoading} impact={impact} />
      </div>
    </ContainedDialog>
  )
}

function DeleteActivityDialog({
  open,
  onOpenChange,
  activity,
  onDeleted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  activity: AgendaActivity
  onDeleted: () => void
}) {
  const preview = useQuery({
    queryKey: ["agenda-activity-delete-preview", activity.id],
    enabled: open,
    queryFn: () => deleteAgendaActivityDryRun(activity.id),
    staleTime: 0,
  })
  const deleteMut = useMutation({
    mutationFn: () => deleteAgendaActivity(activity.id),
    onSuccess: () => {
      toast.success("Activity block deleted")
      onDeleted()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  })
  const impact = preview.data?.wouldDelete

  return (
    <ContainedDialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={`Delete “${activity.name}”?`}
      width={448}
      icon={
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
          <Trash2 className="h-[16px] w-[16px]" />
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-lg px-3.5 text-[13px] font-medium text-plum-700/70 transition hover:bg-plum-100/70 dark:text-plum-200/60 dark:hover:bg-white/5"
          >
            Keep block
          </button>
          <button
            type="button"
            onClick={() => deleteMut.mutate()}
            disabled={deleteMut.isPending}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-rose-600 px-4 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-[13px] leading-relaxed text-plum-700/65 dark:text-plum-200/55">
          This permanently removes the activity block and its child sessions from the schedule.
        </p>
        <DeleteImpactRows loading={preview.isLoading} impact={impact} />
      </div>
    </ContainedDialog>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-medium text-plum-700/65 dark:text-plum-200/45">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  )
}

function CheckRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-plum-800 dark:text-plum-100">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  )
}

function DeleteImpactRows({
  loading,
  impact,
}: {
  loading: boolean
  impact?: {
    items?: number
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}) {
  const rows = [
    ...(impact?.items != null ? [["Child sessions", impact.items] as const] : []),
    ["RSVP / waitlist rows", impact?.rsvps ?? 0] as const,
    ["To-dos", impact?.todos ?? 0] as const,
    ["To-do responses", impact?.todoAssignments ?? 0] as const,
    ["Share links", impact?.shareLinks ?? 0] as const,
    ["Scheduled reminders", impact?.reminders ?? 0] as const,
  ]
  return (
    <div className="space-y-1 rounded-lg border border-plum-200/60 bg-plum-50/50 p-3 text-[12px] dark:border-white/8 dark:bg-white/[0.03]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3">
          <span className="text-plum-700/60 dark:text-plum-200/45">{label}</span>
          <span className="font-mono tabular-nums text-plum-900 dark:text-plum-50">
            {loading ? "..." : value}
          </span>
        </div>
      ))}
    </div>
  )
}

const inputCls =
  "h-9 w-full rounded-lg border border-plum-200/70 bg-white px-3 text-[13px] text-plum-900 outline-none transition focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/15 dark:border-white/10 dark:bg-white/[0.03] dark:text-plum-50"

function toPickerValue(value: string | null): string {
  if (!value) return ""
  try {
    return normalizeWallClockDateTime(value, false)
  } catch {
    return ""
  }
}

function toActionDateTime(value: string): string | null {
  return value ? normalizeWallClockDateTime(value) : null
}

// ── day section (timeline) ──────────────────────────────────────────────────
function DaySection({
  date,
  items,
  emptyGroups,
  groupsById,
  activitiesById,
  locationsById,
  onItemClick,
  onLocationClick,
  onDuplicateGroup,
  onEditGroup,
  onDeleteGroup,
  onEditActivity,
  onDeleteActivity,
  duplicatingGroupId,
  activeId,
}: {
  date: string
  items: AgendaItem[]
  emptyGroups: EmptyAgendaGroup[]
  groupsById: Map<string, AgendaGroup>
  activitiesById: Map<string, AgendaActivity>
  locationsById: Map<string, LocationRow>
  onItemClick: (item: AgendaItem) => void
  onLocationClick: (locationId: string, name: string) => void
  onDuplicateGroup: (group: AgendaGroup) => void
  onEditGroup: (group: AgendaGroup) => void
  onDeleteGroup: (group: AgendaGroup) => void
  onEditActivity: (activity: AgendaActivity) => void
  onDeleteActivity: (activity: AgendaActivity) => void
  duplicatingGroupId: string | null
  activeId: string | null
}) {
  const slots = useMemo(() => clusterTimelineSlots(items, emptyGroups), [items, emptyGroups])
  return (
    <section>
      {/* Sticky day header on pointer devices only — on touch it scrolls with
          the content (no sticky on mobile). */}
      <div className="z-10 -mx-5 flex items-center gap-2 border-b border-plum-100 bg-white/85 px-5 py-2 backdrop-blur [@media(hover:hover)]:sticky [@media(hover:hover)]:top-0 dark:border-white/8 dark:bg-surface/85">
        <h2 className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
          {formatDayLong(date)}
        </h2>
      </div>

      <div className="space-y-3 pt-2">
        {slots.map((slot, idx) => {
          const slotGroup = commonSlotGroup(slot.items, groupsById)
          const slotActivity = slotGroup ? null : commonSlotActivity(slot.items, activitiesById)
          return (
            <div key={idx} className="flex gap-3 pt-1">
              <div className="w-12 shrink-0 pt-1 text-right">
                <div className="text-[12.5px] font-bold tabular-nums text-plum-800 dark:text-plum-100">
                  {formatTime(slot.startsAt)}
                </div>
                {slot.endsAt && (
                  <div className="text-[10px] tabular-nums text-plum-700/45 dark:text-plum-200/35">
                    {formatTime(slot.endsAt)}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                {slot.emptyGroups.length > 0 && (
                  <div className="mb-2 space-y-2">
                    {slot.emptyGroups.map((group) => (
                      <EmptyGroupCard
                        key={group.id}
                        group={group}
                        onEdit={() => onEditGroup(group)}
                        onDelete={() => onDeleteGroup(group)}
                      />
                    ))}
                  </div>
                )}
                {slot.items.length === 0 ? null : slot.items.length === 1 ? (
                  <ItemCard
                    item={slot.items[0]}
                    locationsById={locationsById}
                    onClick={() => onItemClick(slot.items[0])}
                    onLocationClick={onLocationClick}
                    active={activeId === slot.items[0].id}
                  />
                ) : slotGroup ? (
                  <div className="rounded-2xl border-2 border-plum-200/80 bg-plum-50/40 p-2.5 dark:border-white/10 dark:bg-white/[0.02]">
                    <div className="flex flex-wrap items-center gap-2 px-1.5 pb-2">
                      <GitFork className="h-4 w-4 text-plum-700/60 dark:text-plum-300/55" />
                      <span className="text-[13px] font-semibold tracking-tight text-plum-900 dark:text-plum-50">
                        {slotGroup.name}
                      </span>
                      <Chip tone="group">{choiceLabel(slotGroup, slot.items.length)}</Chip>
                      <Menu.Root>
                        <Menu.Trigger
                          className="ml-auto grid h-7 w-7 place-items-center rounded-lg text-plum-700/65 transition hover:bg-white hover:text-plum-900 data-[popup-open]:bg-white dark:text-plum-200/50 dark:hover:bg-white/8 dark:hover:text-plum-50"
                          title="Block actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Menu.Trigger>
                        <Menu.Portal>
                          <Menu.Positioner sideOffset={6} align="end" className="z-[70] outline-none">
                            <Menu.Popup className="mb-pop min-w-[12rem]">
                              <Menu.Item className="mb-mi" onClick={() => onEditGroup(slotGroup)}>
                                <Pencil className="mb-mii" />
                                Edit block
                              </Menu.Item>
                              <Menu.Item
                                className="mb-mi"
                                disabled={duplicatingGroupId === slotGroup.id}
                                onClick={() => onDuplicateGroup(slotGroup)}
                              >
                                {duplicatingGroupId === slotGroup.id ? (
                                  <Loader2 className="mb-mii animate-spin" />
                                ) : (
                                  <Copy className="mb-mii" />
                                )}
                                Duplicate block
                              </Menu.Item>
                              <Menu.Item className="mb-mi mb-danger" onClick={() => onDeleteGroup(slotGroup)}>
                                <Trash2 className="mb-mii" />
                                Delete block
                              </Menu.Item>
                            </Menu.Popup>
                          </Menu.Positioner>
                        </Menu.Portal>
                      </Menu.Root>
                    </div>
                    {/* Concurrent items stack VERTICALLY on narrow; side-by-side
                        only when there's horizontal room. items-start keeps a
                        shorter card top-aligned with its taller row-mate (one
                        may have a wrapped location line). */}
                    <div className="grid items-start gap-2 @2xl:grid-cols-2">
                      {slot.items.map((it) => (
                        <ItemCard
                          key={it.id}
                          item={it}
                          locationsById={locationsById}
                          onClick={() => onItemClick(it)}
                          onLocationClick={onLocationClick}
                          active={activeId === it.id}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                ) : slotActivity ? (
                  <div className="rounded-2xl border-2 border-plum-200/80 bg-plum-50/40 p-2.5 dark:border-white/10 dark:bg-white/[0.02]">
                    <div className="flex flex-wrap items-center gap-2 px-1.5 pb-2">
                      <GitFork className="h-4 w-4 text-plum-700/60 dark:text-plum-300/55" />
                      <span className="text-[13px] font-semibold tracking-tight text-plum-900 dark:text-plum-50">
                        {slotActivity.name}
                      </span>
                      <Chip tone="group">{activityChoiceLabel(slotActivity, slot.items.length)}</Chip>
                      <Menu.Root>
                        <Menu.Trigger
                          className="ml-auto grid h-7 w-7 place-items-center rounded-lg text-plum-700/65 transition hover:bg-white hover:text-plum-900 data-[popup-open]:bg-white dark:text-plum-200/50 dark:hover:bg-white/8 dark:hover:text-plum-50"
                          title="Block actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Menu.Trigger>
                        <Menu.Portal>
                          <Menu.Positioner sideOffset={6} align="end" className="z-[70] outline-none">
                            <Menu.Popup className="mb-pop min-w-[12rem]">
                              <Menu.Item className="mb-mi" onClick={() => onEditActivity(slotActivity)}>
                                <Pencil className="mb-mii" />
                                Edit block
                              </Menu.Item>
                              <Menu.Item className="mb-mi mb-danger" onClick={() => onDeleteActivity(slotActivity)}>
                                <Trash2 className="mb-mii" />
                                Delete block
                              </Menu.Item>
                            </Menu.Popup>
                          </Menu.Positioner>
                        </Menu.Portal>
                      </Menu.Root>
                    </div>
                    <div className="grid items-start gap-2 @2xl:grid-cols-2">
                      {slot.items.map((it) => (
                        <ItemCard
                          key={it.id}
                          item={it}
                          locationsById={locationsById}
                          onClick={() => onItemClick(it)}
                          onLocationClick={onLocationClick}
                          active={activeId === it.id}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="grid items-start gap-2 @2xl:grid-cols-2">
                    {slot.items.map((it) => (
                      <ItemCard
                        key={it.id}
                        item={it}
                        locationsById={locationsById}
                        onClick={() => onItemClick(it)}
                        active={activeId === it.id}
                        compact
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

type TimelineSlot = {
  startsAt: string
  endsAt: string | null
  items: AgendaItem[]
  emptyGroups: EmptyAgendaGroup[]
}

function clusterTimelineSlots(items: AgendaItem[], emptyGroups: EmptyAgendaGroup[]): TimelineSlot[] {
  const slots = new Map<string, TimelineSlot>()
  for (const slot of clusterByStartTime(items)) {
    slots.set(slot.startsAt, { ...slot, emptyGroups: [] })
  }
  for (const group of emptyGroups) {
    const existing = slots.get(group.startsAt)
    if (existing) {
      existing.emptyGroups.push(group)
      if (group.endsAt && group.endsAt > (existing.endsAt ?? "")) existing.endsAt = group.endsAt
    } else {
      slots.set(group.startsAt, {
        startsAt: group.startsAt,
        endsAt: group.endsAt ?? null,
        items: [],
        emptyGroups: [group],
      })
    }
  }
  return Array.from(slots.values()).sort((a, b) => a.startsAt.localeCompare(b.startsAt))
}

function EmptyGroupCard({
  group,
  onEdit,
  onDelete,
}: {
  group: EmptyAgendaGroup
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-plum-200/80 bg-plum-50/35 p-3 dark:border-white/10 dark:bg-white/[0.02]">
      <div className="flex flex-wrap items-center gap-2">
        <GitFork className="h-4 w-4 text-plum-700/55 dark:text-plum-300/50" />
        <span className="text-[13px] font-semibold tracking-tight text-plum-900 dark:text-plum-50">
          {group.name}
        </span>
        <Chip tone="draft">No sessions yet</Chip>
        <Chip tone="group">{group.selectionRule === "multi" ? "Pick any" : "Pick one"}</Chip>
        <Menu.Root>
          <Menu.Trigger
            className="ml-auto grid h-7 w-7 place-items-center rounded-lg text-plum-700/65 transition hover:bg-white hover:text-plum-900 data-[popup-open]:bg-white dark:text-plum-200/50 dark:hover:bg-white/8 dark:hover:text-plum-50"
            title="Block actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner sideOffset={6} align="end" className="z-[70] outline-none">
              <Menu.Popup className="mb-pop min-w-[12rem]">
                <Menu.Item className="mb-mi" onClick={onEdit}>
                  <Pencil className="mb-mii" />
                  Edit block
                </Menu.Item>
                <Menu.Item className="mb-mi mb-danger" onClick={onDelete}>
                  <Trash2 className="mb-mii" />
                  Delete block
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-plum-700/55 dark:text-plum-200/40">
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {formatTime(group.startsAt)}
          {group.endsAt ? ` – ${formatTime(group.endsAt)}` : ""}
        </span>
      </div>
    </div>
  )
}

// ── item card ───────────────────────────────────────────────────────────────
function ItemCard({
  item,
  locationsById,
  onClick,
  onLocationClick,
  active,
  compact,
}: {
  item: AgendaItem
  locationsById: Map<string, LocationRow>
  onClick: () => void
  onLocationClick?: (locationId: string, name: string) => void
  active?: boolean
  compact?: boolean
}) {
  const isCancelled = item.status === "cancelled"
  const isDraft = item.status === "draft"
  const isPrivate = item.visibility === "private"
  // Full parent path (for the hover tooltip) + leaf/child room name (shown).
  const locationName = locationPath(item.locationId, locationsById)
  const locationLeaf =
    (item.locationId ? locationsById.get(item.locationId)?.name : null) ?? locationName

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border bg-white p-3.5 text-left shadow-fluent-sm transition dark:bg-white/[0.03] ${
        active
          ? "border-[var(--accent)] ring-1 ring-[var(--accent)]/30"
          : isDraft
            ? "border-dashed border-plum-300/70 hover:border-plum-400 dark:border-white/12"
            : "border-plum-200/70 hover:border-plum-300 hover:shadow-fluent dark:border-white/8"
      } ${isCancelled ? "opacity-70" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {isPrivate && (
          <Lock className="h-3.5 w-3.5 shrink-0 text-plum-700/40 dark:text-plum-200/30" aria-label="Private" />
        )}
        <span
          className={`font-semibold tracking-tight text-plum-900 dark:text-plum-50 ${
            compact ? "text-[14px]" : "text-[14.5px]"
          } ${isCancelled ? "text-plum-700/55 line-through dark:text-plum-200/45" : ""}`}
        >
          {item.name || "Untitled session"}
        </span>
        <AttendanceChip item={item} />
        {isCancelled && <Chip tone="cancelled">Cancelled</Chip>}
        {isDraft && <Chip tone="draft">Draft</Chip>}
      </div>

      {item.description && !compact && (
        <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-plum-700/60 dark:text-plum-200/45">
          {item.description}
        </p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-plum-700/60 dark:text-plum-200/45">
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {formatTime(item.startsAt)}
          {item.endsAt ? ` – ${formatTime(item.endsAt)}` : ""}
        </span>
        {locationName &&
          (item.locationId && onLocationClick ? (
            // Clickable location pill — opens the location record in the right
            // rail. role=button + stopPropagation so it never triggers the card.
            <span
              role="button"
              tabIndex={0}
              title={`Open ${locationName}`}
              onClick={(e) => {
                e.stopPropagation()
                onLocationClick(item.locationId!, locationName)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  onLocationClick(item.locationId!, locationName)
                }
              }}
              className="flex max-w-full items-start gap-1 rounded-md bg-plum-50 px-1.5 py-0.5 text-left transition hover:bg-plum-100 hover:text-plum-800 dark:bg-white/[0.05] dark:hover:bg-white/[0.1] dark:hover:text-plum-50"
            >
              <MapPin className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 break-words">{locationLeaf}</span>
            </span>
          ) : (
            <span className="flex max-w-full items-start gap-1 rounded-md bg-plum-50 px-1.5 py-0.5 dark:bg-white/[0.05]">
              <MapPin className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 break-words">{locationLeaf}</span>
            </span>
          ))}
        {item.capacity != null && (
          <span className="flex items-center gap-1 rounded-md bg-plum-50 px-1.5 py-0.5 dark:bg-white/[0.05]">
            <Users className="h-3.5 w-3.5 shrink-0" />
            {item.capacity} {item.capacity === 1 ? "spot" : "spots"}
          </span>
        )}
      </div>
    </button>
  )
}

// ── chips ───────────────────────────────────────────────────────────────────
const CHIP_TONE: Record<ChipTone, string> = {
  required: "bg-rose-500/12 text-rose-600 dark:text-rose-400",
  rsvp: "bg-amber-500/12 text-amber-700 dark:text-amber-300",
  invite: "bg-violet-500/12 text-violet-700 dark:text-violet-300",
  open: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  display: "bg-sky-500/12 text-sky-700 dark:text-sky-300",
  draft: "bg-plum-100 text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55",
  cancelled: "bg-rose-500/12 text-rose-600 dark:text-rose-400",
  group: "bg-plum-200/60 text-plum-800 dark:bg-white/10 dark:text-plum-100",
}

function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${CHIP_TONE[tone]}`}>
      {children}
    </span>
  )
}

function AttendanceChip({ item }: { item: AgendaItem }) {
  const { tone, label } = attendanceChip(item)
  return <Chip tone={tone}>{label}</Chip>
}

function activityChoiceLabel(activity: AgendaActivity, count: number): string {
  const min = activity.minPerAttendee ?? 0
  const max = activity.maxPerAttendee ?? 1
  if (min <= 0 && max >= count) return `Pick any of ${count}`
  if (min <= 0) return `Pick up to ${max}`
  if (min === max) return `Pick ${min}`
  return `Pick ${min}-${max}`
}

// ── shared states ────────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="mx-auto max-w-[920px] space-y-3 px-5 py-6">
      <div className="h-5 w-40 animate-pulse rounded-md bg-plum-100 dark:bg-white/10" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="h-12 w-12 shrink-0 animate-pulse rounded-md bg-plum-100 dark:bg-white/10" />
          <div className="h-[78px] flex-1 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
        </div>
      ))}
    </div>
  )
}

function CenteredMessage({ icon, title, blurb }: { icon: ReactNode; title: string; blurb: string }) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
          {icon}
        </div>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">{title}</h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
          {blurb}
        </p>
      </div>
    </div>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────
